import { type FC } from "react";
import classNames from "classnames";
import { ActionIconButton } from "~/common/components/Button/ActionIconButton";
import { canSeePriceRefColumn, canSeeStaticPriceRefColumn } from "../utils/helpers";
import { useFieldArray, useFormContext } from "react-hook-form";
import { type QuickEntryForm, type QuickEntryItem } from "../types/models";
import { getEmptyRow } from "../constants/constants";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import { QuickEntryTableRow } from "./QuickEntryTableRow";
import { userCanSeeEndCustomer, userCanSeePriceReference } from "~/features/cart/cart/components/LineItemStaticParams";
import { LinkButton } from "~/common/components/Link";
import { useOnMount } from "~/common/hooks/useOnMount";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_DESKTOP_S, MEDIA_QUERY_MIN_TABLET } from "~/common/utils/screenSizes";
import { useTranslation } from "react-i18next";
import { userIsCustomer } from "~/domains/user/utils/roles";

import { isNonNull } from "~/common/utils/reducers";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/Delete";
import { QuickEntryTableRoot } from "~/features/quickEntry/components/QuickEntryTable.styles";

interface Props {}

export const QuickEntryTable: FC<Props> = () => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);
  const isPhone = !isMinTablet;

  const showPriceRefColumn = canSeePriceRefColumn() || canSeeStaticPriceRefColumn();

  const { control, watch, setValue, getValues } = useFormContext<QuickEntryForm>();

  // @ts-ignore
  const { fields, remove, append } = useFieldArray<QuickEntryItem>({ control, name: "items" });

  useOnMount(() => {
    // @ts-ignore
    append(getEmptyRow());
  });

  const allData = watch();

  const allCommentsOpen = !allData.items?.some((item) => !item.isCommentVisible);
  const commentIcon = allCommentsOpen ? <CloseIcon  /> : <CommentIcon  />;

  const canMakeComments =
    allData.items
      ?.filter(isNonNull)
      .map((item) => item.product)
      .filter(isNonNull)
      .some((product) => canUseCommentsForProduct(product.marketType)) ?? false;

  const onRemoveAllItems = () => {
    setValue("items", [getEmptyRow()]);
  };
  const toggleAllCommentsVisiblity = () => {
    const newVal = !allCommentsOpen;
    const items = getValues().items;
    items?.forEach((item, index) => {
      setValue(`items.${index}.isCommentVisible`, newVal);
    });
  };

  let priceReferenceEndCustomerTitle = "";
  if (userCanSeeEndCustomer() && showPriceRefColumn) {
    priceReferenceEndCustomerTitle = "productDetailPage.endCustomerLabel";
  } else if (userCanSeePriceReference() && showPriceRefColumn) {
    priceReferenceEndCustomerTitle = "cartPage.table.header.priceReference.title";
  }

  const appendNextRow = () => {
    let date = new Date();
    let priceReference = "-";
    if (fields.length > 0) {
      const lastField = getValues(`items.${fields.length - 1}`) as QuickEntryItem;
      if (lastField && lastField.requestedDeliveryDate) {
        date = lastField.requestedDeliveryDate;
      }
      if (lastField && lastField.priceReference) {
        priceReference = lastField.priceReference;
      }
    }
    // @ts-ignore
    append({ ...getEmptyRow(), requestedDeliveryDate: date, priceReference: priceReference });
  };

  const isUserCustomer = userIsCustomer();

  return (
    <QuickEntryTableRoot
      className={classNames("scha__bulk-preview__table", showPriceRefColumn && "scha__bulk-preview__table--priceRef")}
    >
      <div className={"scha__bulk-preview__table__header"}>
        <div>{t("wishlistPage.table.header.number")}</div>
        <div>{t("bulkUpload.table.header.productId")}</div>
        <div>{t("cartPage.table.header.quantity.title")}</div>
        {isUserCustomer && <div>{t("cartPage.table.header.delivery.title")}</div>}

        <div>{t(priceReferenceEndCustomerTitle)}</div>

        <div className={"d-flex flex-justify-end"}>
          {canMakeComments && (
            <ActionIconButton
              icon={commentIcon}
              onIconClick={toggleAllCommentsVisiblity}
              data-test-id={"btn-quick-entry-toggle-all-comments"}
            />
          )}
          <ActionIconButton
            className={"ml-3"}
            icon={<DeleteIcon  />}
            onIconClick={onRemoveAllItems}
            data-test-id={"btn-quick-remove-all"}
          />
        </div>
      </div>
      <div className={"scha__bulk-preview__table__body"}>
        {fields.map((item, index) => (
          <QuickEntryTableRow
            key={item.id}
            // @ts-ignore
            item={item as QuickEntryItem}
            index={index}
            onRemove={() => {
              if (fields.length > 1) {
                remove(index);
              } else {
                onRemoveAllItems();
              }
            }}
          />
        ))}
      </div>
      <div className="scha__bulk-preview__table__row add-new-row-button">
        {!isPhone && <div className="scha__bulk-preview__table__left-side" />}
        <LinkButton
          type={"button"}
          onFocus={isDesktop ? appendNextRow : undefined}
          onClick={!isDesktop ? appendNextRow : undefined}
          data-test-id={"btn-quick-entry-add-new-product"}
          iconBefore={<AddIcon  />}
        >
          {t("cartPage.table.copyRow")}
        </LinkButton>
      </div>
    </QuickEntryTableRoot>
  );
};
